import React, { useCallback, useMemo, useState } from 'react';
import {
    DataGrid,
    GridActionsCellItem,
    GridColDef,
    GridFilterModel,
    GridPaginationModel,
    GridRowParams,
    GridRowSelectionModel,
    GridSlots,
    GridSortModel,
    GridToolbarContainer,
    getGridStringOperators,
} from '@mui/x-data-grid';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { RouteCard, RouteCardsPagination, RouteCardsQueryParams } from '../interfaces';
import { transformKeyToTitle } from '../utils/transformKeyToTitle';
import { formatDate } from '../utils/formatDate';
import {
    getRouteCardsFilterQueryPatch,
    getRouteCardsSortQueryPatch,
    isRouteCardServerFilterable,
    isRouteCardServerSortable,
} from '../utils/routeCardsTable';
import { useManageGrid } from '../hooks/useManageGrid';
import { CopyTextCell } from './GridCells/CopyTextCell';
import { EditRouteCardDialog } from './EditRouteCardDialog';
import { getRouteCardPdfDownloadLink } from '../api/getRouteCardPdfDownloadLink';
import { mergeRouteCardPdfs } from '../api/mergeRouteCardPdfs';
import { downloadFileFromUrl } from '../utils/downloadFileFromUrl';

interface OnboardDevicesRouteCardsTableGridProps {
    routeCards: RouteCard[];
    pagination: RouteCardsPagination;
    loading: boolean;
    error: string | null;
    query: RouteCardsQueryParams;
    onQueryChange: (queryPatch: Partial<RouteCardsQueryParams>) => void;
    onReload: () => void;
}

const ROUTE_CARD_COLUMN_DEFS = [
    { key: 'deviceId', renderCell: CopyTextCell },
    { key: 'status', renderCell: CopyTextCell },
    { key: 'createdAt', valueFormatter: formatDate },
    { key: 'createdBy', renderCell: CopyTextCell },
];

interface ToolbarProps {
    onReload: () => void;
    selectedCount: number;
    onPrint: () => void;
    isPrinting: boolean;
}

function Toolbar({ onReload, selectedCount, onPrint, isPrinting }: ToolbarProps) {
    return (
        <GridToolbarContainer>
            <Button startIcon={<RefreshIcon />} color="primary" onClick={onReload}>
                Reload
            </Button>
            <Button
                startIcon={isPrinting ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
                color="primary"
                onClick={onPrint}
                disabled={selectedCount === 0 || isPrinting}
            >
                {isPrinting ? 'Друкуємо...' : `Друк${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
            </Button>
        </GridToolbarContainer>
    );
}

export const OnboardDevicesRouteCardsTableGrid = ({
    routeCards,
    pagination,
    loading,
    error,
    query,
    onQueryChange,
    onReload,
}: OnboardDevicesRouteCardsTableGridProps) => {
    const [editingRouteCard, setEditingRouteCard] = useState<RouteCard | null>(null);
    const [downloadingRowId, setDownloadingRowId] = useState<string | null>(null);
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
    const [isPrinting, setIsPrinting] = useState(false);
    const [printError, setPrintError] = useState<string | null>(null);

    const {
        sortModel,
        columnsConfig,
        columnsWidth,
        gridFilterModel,
        handleColumnVisibilityChange,
        handleColumnWidthChange,
        handleSortModelChange,
        handleFilterModelChange,
        onCellKeyDown,
        gridStateSynced,
    } = useManageGrid('onboard-device-route-cards');

    const sanitizedSortModel = useMemo<GridSortModel>(() => {
        return ((sortModel || []) as GridSortModel).filter((item) => isRouteCardServerSortable(item.field));
    }, [sortModel]);

    const handleViewClick = useCallback((routeCard: RouteCard) => {
        setEditingRouteCard(routeCard);
    }, []);

    const handleDownloadPdf = useCallback(
        async (routeCard: RouteCard) => {
            if (downloadingRowId) return;
            setDownloadingRowId(routeCard.id);
            try {
                const presignedUrl = await getRouteCardPdfDownloadLink(routeCard.id);
                const keyParts = presignedUrl.split('/');
                await downloadFileFromUrl(presignedUrl, keyParts[keyParts.length - 1]);
            } finally {
                setDownloadingRowId(null);
            }
        },
        [downloadingRowId],
    );

    const handlePrint = useCallback(async () => {
        if (isPrinting || rowSelectionModel.length === 0) return;
        setPrintError(null);
        setIsPrinting(true);
        try {
            const ids = rowSelectionModel.map(String);
            const presignedUrl = await mergeRouteCardPdfs(ids);
            window.open(presignedUrl, '_blank');
        } catch {
            setPrintError('Не вдалося згенерувати зведений PDF. Переконайтесь, що обрані картки мають PDF.');
        } finally {
            setIsPrinting(false);
        }
    }, [isPrinting, rowSelectionModel]);

    const columns: GridColDef[] = useMemo(() => {
        const dataColumns: GridColDef[] = ROUTE_CARD_COLUMN_DEFS.map((col) => {
            const isServerFilterable = isRouteCardServerFilterable(col.key);

            return {
                field: col.key,
                headerName: (col as any).headerName || transformKeyToTitle(col.key),
                renderCell: (col as any).renderCell,
                valueFormatter: (col as any).valueFormatter,
                width: columnsWidth[col.key] || 220,
                align: 'left',
                headerAlign: 'left',
                sortable: isRouteCardServerSortable(col.key),
                filterable: isServerFilterable,
                filterOperators: isServerFilterable
                    ? getGridStringOperators().filter((op) => op.value === 'equals')
                    : undefined,
            };
        });

        const actionsColumn: GridColDef = {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            getActions: (params: GridRowParams<RouteCard>) => {
                const isDownloading = downloadingRowId === params.row.id;
                const hasPdf = Boolean(params.row.pdfUrl);

                return [
                    <GridActionsCellItem
                        key="view"
                        icon={<VisibilityIcon />}
                        label="Переглянути"
                        onClick={() => handleViewClick(params.row)}
                    />,
                    ...(hasPdf
                        ? [
                              <GridActionsCellItem
                                  key="download-pdf"
                                  icon={isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
                                  label="Завантажити PDF"
                                  onClick={() => handleDownloadPdf(params.row)}
                                  disabled={Boolean(downloadingRowId)}
                              />,
                          ]
                        : []),
                ];
            },
        };

        return [...dataColumns, actionsColumn];
    }, [columnsWidth, handleViewClick, handleDownloadPdf, downloadingRowId]);

    const rows = useMemo(() => {
        return routeCards.map((routeCard) => ({
            ...routeCard,
            id: routeCard.id,
        }));
    }, [routeCards]);

    const handlePaginationModelChange = useCallback(
        (model: GridPaginationModel) => {
            onQueryChange({ page: model.page + 1, pageSize: model.pageSize });
        },
        [onQueryChange],
    );

    const handleServerSortModelChange = useCallback(
        (model: GridSortModel) => {
            const nextSortModel = model.filter((item) => isRouteCardServerSortable(item.field));
            handleSortModelChange(nextSortModel);
            onQueryChange({ page: 1, ...getRouteCardsSortQueryPatch(nextSortModel) });
        },
        [handleSortModelChange, onQueryChange],
    );

    const handleServerFilterModelChange = useCallback(
        (model: GridFilterModel) => {
            handleFilterModelChange(model);
            onQueryChange({ page: 1, ...getRouteCardsFilterQueryPatch(model) });
        },
        [handleFilterModelChange, onQueryChange],
    );

    const handleEditDialogClose = useCallback(() => {
        setEditingRouteCard(null);
    }, []);

    const handleEditSuccess = useCallback(() => {
        setEditingRouteCard(null);
        onReload();
    }, [onReload]);

    const ToolbarSlot = useCallback(
        () => (
            <Toolbar
                onReload={onReload}
                selectedCount={rowSelectionModel.length}
                onPrint={handlePrint}
                isPrinting={isPrinting}
            />
        ),
        [onReload, rowSelectionModel.length, handlePrint, isPrinting],
    );

    return (
        <>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {printError && (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPrintError(null)}>
                    {printError}
                </Alert>
            )}
            {gridStateSynced && (
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    pagination
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    rowCount={pagination.totalItems}
                    paginationModel={{
                        page: Math.max(query.page - 1, 0),
                        pageSize: query.pageSize,
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    onPaginationModelChange={handlePaginationModelChange}
                    checkboxSelection
                    keepNonExistentRowsSelected
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={setRowSelectionModel}
                    slots={{ toolbar: ToolbarSlot as GridSlots['toolbar'] }}
                    disableRowSelectionOnClick
                    onCellKeyDown={onCellKeyDown}
                    filterModel={gridFilterModel}
                    sortModel={sanitizedSortModel}
                    onSortModelChange={handleServerSortModelChange}
                    onColumnWidthChange={handleColumnWidthChange}
                    onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    onFilterModelChange={handleServerFilterModelChange}
                    columnVisibilityModel={columnsConfig}
                />
            )}

            {editingRouteCard && (
                <EditRouteCardDialog
                    open={Boolean(editingRouteCard)}
                    routeCard={editingRouteCard}
                    onClose={handleEditDialogClose}
                    onSuccess={handleEditSuccess}
                />
            )}
        </>
    );
};
